import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
  Progress,
} from '@forgenetwork/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Activity,
  CheckCircle2,
  Clock,
  Server,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Download,
  AlertTriangle,
  Zap,
} from 'lucide-react';

export const Route = createFileRoute('/admin/reliability')({
  component: AdminReliabilityPage,
});

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down' | 'unknown';
  latency: number;
  uptime: number;
  lastCheck: string;
}

interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'down';
  services: ServiceHealth[];
  timestamp: string;
}

interface ServiceAvailability {
  name: string;
  availability: number;
  uptimeMinutes: number;
  downtimeMinutes: number;
  incidents: number;
}

interface Incident {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  service: string;
  startedAt: string;
  resolvedAt: string | null;
  duration: number | null;
  updates: Array<{
    id: string;
    message: string;
    status: string;
    createdAt: string;
  }>;
}

function AdminReliabilityPage(): React.JSX.Element {
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Fetch system health
  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = useQuery({
    queryKey: ['admin', 'system', 'health'],
    queryFn: async () => {
      return api.get<SystemHealth>('/admin/system/health');
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch service availability
  const { data: availabilityData, isLoading: availabilityLoading, refetch: refetchAvailability } = useQuery({
    queryKey: ['admin', 'system', 'availability'],
    queryFn: async () => {
      return api.get<{ services: ServiceAvailability[]; period: string }>('/admin/system/availability');
    },
  });

  // Fetch recent incidents
  const { data: incidentsData, isLoading: incidentsLoading, refetch: refetchIncidents } = useQuery({
    queryKey: ['admin', 'system', 'incidents'],
    queryFn: async () => {
      return api.get<{ incidents: Incident[] }>('/admin/system/incidents');
    },
  });

  const isLoading = healthLoading || availabilityLoading || incidentsLoading;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([refetchHealth(), refetchAvailability(), refetchIncidents()]);
    setIsRefreshing(false);
  };

  // Calculate overview metrics
  const overviewMetrics = React.useMemo(() => {
    const services = healthData?.services ?? [];
    const availability = availabilityData?.services ?? [];
    const incidents = incidentsData?.incidents ?? [];

    const healthyServices = services.filter(s => s.status === 'healthy').length;
    const totalServices = services.length || 1;
    const overallUptime = availability.length > 0
      ? availability.reduce((sum, s) => sum + s.availability, 0) / availability.length
      : 99.9;
    const activeIncidents = incidents.filter(i => i.status !== 'resolved').length;
    const avgLatency = services.length > 0
      ? Math.round(services.reduce((sum, s) => sum + s.latency, 0) / services.length)
      : 0;

    // Calculate MTTR (Mean Time To Recovery) from resolved incidents
    const resolvedIncidents = incidents.filter(i => i.resolvedAt && i.duration);
    const mttr = resolvedIncidents.length > 0
      ? Math.round(resolvedIncidents.reduce((sum, i) => sum + (i.duration ?? 0), 0) / resolvedIncidents.length)
      : 0;

    return {
      healthyServices,
      totalServices,
      overallUptime,
      activeIncidents,
      avgLatency,
      mttr,
    };
  }, [healthData, availabilityData, incidentsData]);

  const getStatusBadge = (status: ServiceHealth['status']) => {
    switch (status) {
      case 'healthy':
        return <Badge className="bg-green-500">Healthy</Badge>;
      case 'degraded':
        return <Badge className="bg-yellow-500">Degraded</Badge>;
      case 'down':
        return <Badge variant="destructive">Down</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getIncidentStatusBadge = (status: Incident['status']) => {
    switch (status) {
      case 'investigating':
        return <Badge variant="destructive">Investigating</Badge>;
      case 'identified':
        return <Badge className="bg-orange-500">Identified</Badge>;
      case 'monitoring':
        return <Badge className="bg-yellow-500">Monitoring</Badge>;
      case 'resolved':
        return <Badge className="bg-green-500">Resolved</Badge>;
    }
  };

  const getSeverityBadge = (severity: Incident['severity']) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500">Medium</Badge>;
      case 'low':
        return <Badge variant="secondary">Low</Badge>;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getUptimeTrend = (uptime: number) => {
    if (uptime >= 99.9) return { icon: TrendingUp, color: 'text-green-500' };
    if (uptime >= 99.0) return { icon: Minus, color: 'text-yellow-500' };
    return { icon: TrendingDown, color: 'text-red-500' };
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reliability Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Monitor system uptime, service health, and incident response
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Uptime</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{overviewMetrics.overallUptime.toFixed(2)}%</div>
                  <p className="text-xs text-muted-foreground">Last 30 days</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Service Health</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {overviewMetrics.healthyServices}/{overviewMetrics.totalServices}
                  </div>
                  <p className="text-xs text-muted-foreground">Services healthy</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Incidents</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{overviewMetrics.activeIncidents}</div>
                  <p className="text-xs text-muted-foreground">Requiring attention</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Latency</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{overviewMetrics.avgLatency}ms</div>
                  <p className="text-xs text-muted-foreground">Response time</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">MTTR</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {overviewMetrics.mttr > 0 ? formatDuration(overviewMetrics.mttr) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Mean time to recovery</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">SLA Status</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {overviewMetrics.overallUptime >= 99.9 ? 'Met' : 'At Risk'}
                  </div>
                  <p className="text-xs text-muted-foreground">99.9% target</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Service Health Status */}
          <Card>
            <CardHeader>
              <CardTitle>Service Health</CardTitle>
              <CardDescription>Real-time status of all services</CardDescription>
            </CardHeader>
            <CardContent>
              {healthLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : healthData?.services && healthData.services.length > 0 ? (
                <div className="space-y-3">
                  {healthData.services.map((service) => {
                    const trend = getUptimeTrend(service.uptime);
                    const TrendIcon = trend.icon;
                    return (
                      <div
                        key={service.name}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            service.status === 'healthy' ? 'bg-green-500' :
                            service.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'
                          }`} />
                          <div>
                            <div className="font-medium">{service.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {service.latency}ms latency
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-medium">{service.uptime.toFixed(2)}%</span>
                              <TrendIcon className={`h-3 w-3 ${trend.color}`} />
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatTimeAgo(service.lastCheck)}
                            </div>
                          </div>
                          {getStatusBadge(service.status)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Server className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No service data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Service Availability */}
          <Card>
            <CardHeader>
              <CardTitle>Service Availability</CardTitle>
              <CardDescription>30-day availability metrics</CardDescription>
            </CardHeader>
            <CardContent>
              {availabilityLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : availabilityData?.services && availabilityData.services.length > 0 ? (
                <div className="space-y-4">
                  {availabilityData.services.map((service) => (
                    <div key={service.name} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{service.name}</span>
                        <span className="text-sm">{service.availability.toFixed(2)}%</span>
                      </div>
                      <Progress value={service.availability} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{service.incidents} incidents</span>
                        <span>{service.downtimeMinutes}m downtime</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No availability data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Incidents */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Incidents</CardTitle>
            <CardDescription>Latest system incidents and their resolution status</CardDescription>
          </CardHeader>
          <CardContent>
            {incidentsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : incidentsData?.incidents && incidentsData.incidents.length > 0 ? (
              <div className="space-y-4">
                {incidentsData.incidents.map((incident) => (
                  <div
                    key={incident.id}
                    className="p-4 rounded-lg border space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h2 className="font-medium">{incident.title}</h2>
                          {getSeverityBadge(incident.severity)}
                          {getIncidentStatusBadge(incident.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Service: {incident.service}
                        </p>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <div>{formatTimeAgo(incident.startedAt)}</div>
                        {incident.duration && (
                          <div>Duration: {formatDuration(incident.duration)}</div>
                        )}
                      </div>
                    </div>
                    {incident.updates.length > 0 && (
                      <div className="pl-4 border-l-2 border-muted space-y-2">
                        {incident.updates.slice(0, 2).map((update) => (
                          <div key={update.id} className="text-sm">
                            <span className="text-muted-foreground">
                              {formatTimeAgo(update.createdAt)}:
                            </span>{' '}
                            {update.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mb-2 text-green-500" />
                <p>No recent incidents</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
